/**
 * Arthaus Marketplace — PostHog storefront events.
 *
 * The client is booted by snippets/posthog.liquid (loader + init + surface super-property).
 * This file exposes the capture helper and wires meaningful storefront events. It federates
 * a third analytics destination alongside the existing GA4/Google-Ads (gtag) and $AXON scout
 * tracking — it never removes or alters those.
 *
 * Shared 4-level schema: surface.category.object.action. The PostHog event NAME is the flat
 * `<object>_<action>` snake_case form (artwork_view, cart_add, collection_filter, …), matching
 * the AMS-admin PostHog module and the Easel (sites/) harness so events line up across surfaces.
 *
 * Every capture carries: surface, event_category, event_object, event_action, event_label,
 * value, plus domain props (handle, title, variant_id, price, collection, query, …).
 *
 * Purely client-side. Nothing here touches the /search → picasso-concierge crawl path
 * server-side; search_submit fires only on a deliberate shopper action (Enter / suggestion tap).
 */
(function () {
  'use strict';

  var SURFACE = 'marketplace';

  // object → default event_category. Callers may override via props.event_category.
  var CATEGORY = {
    artwork: 'discovery',
    collection: 'discovery',
    search: 'discovery',
    cart: 'commerce',
    checkout: 'commerce'
  };

  /**
   * window.ahPosthog(object, action, props)
   * Emits `<object>_<action>` with the standard schema envelope. No-op (and never throws)
   * when PostHog isn't present — the loader stub queues calls, so this is safe pre-load too.
   */
  /**
   * Storefront event bus — every sink subscribes here instead of binding its own listeners.
   * window.ahTrack(name, props) dispatches `ah:track` ({ event, props }) on window and keeps
   * the last 50 events in window.__ahTrackBuffer so a sink that loads later can replay them
   * (assets/google-ads.js does). ahPosthog publishes every event it captures; bus-only events
   * (e.g. cart_lines_added) go through ahTrack directly and never reach PostHog.
   */
  var buffer = window.__ahTrackBuffer = window.__ahTrackBuffer || [];
  function ahTrack(name, props) {
    try {
      var evt = { event: name, props: props || {} };
      buffer.push(evt);
      if (buffer.length > 50) buffer.shift();
      window.dispatchEvent(new CustomEvent('ah:track', { detail: evt }));
    } catch (e) {
      /* a sink must never break the storefront */
    }
  }
  window.ahTrack = ahTrack;

  function ahPosthog(object, action, props) {
    props = props || {};
    ahTrack(object + '_' + action, props);
    if (typeof window.posthog === 'undefined' || typeof window.posthog.capture !== 'function') return;
    try {
      var category = props.event_category || CATEGORY[object] || 'engagement';
      var payload = {
        surface: SURFACE,
        event_category: category,
        event_object: object,
        event_action: action,
        event_label: props.event_label != null ? props.event_label : (props.title || props.query || props.handle || ''),
        value: props.value
      };
      if (object === 'cart' && window.ahExperienceAnalytics) {
        var context = window.ahExperienceAnalytics.contextForProduct(props.product_id, props.handle);
        for (var field in context) payload[field] = context[field];
      }
      for (var k in props) {
        if (Object.prototype.hasOwnProperty.call(props, k) && payload[k] === undefined) payload[k] = props[k];
      }
      window.posthog.capture(object + '_' + action, payload);
    } catch (e) {
      /* analytics must never break the storefront */
    }
  }
  window.ahPosthog = ahPosthog;

  /**
   * Begin session replay for the Picasso AI-search experience. Replay is disabled by default
   * on the storefront (see snippets/posthog.liquid); this turns it on for the current session
   * the moment a shopper actually uses AI search, so we capture that experience end to end
   * without recording every browse session. Idempotent + guarded; requires Session Replay
   * enabled on the PostHog project.
   */
  function startAiSearchReplay() {
    try {
      var ph = window.posthog;
      if (!ph || typeof ph.startSessionRecording !== 'function') return;
      if (typeof ph.sessionRecordingStarted === 'function' && ph.sessionRecordingStarted()) return;
      ph.startSessionRecording();
      if (typeof ph.register === 'function') ph.register({ replay_reason: 'ai_search' });
    } catch (e) {
      /* never break the storefront for analytics */
    }
  }
  window.ahStartAiSearchReplay = startAiSearchReplay;

  // ── Page-load events (context set server-side in snippets/posthog.liquid) ──────────────
  function firePageEvents() {
    var ctx = window.__ahAnalytics || {};

    // Product detail → artwork_view
    if (ctx.product && ctx.product.handle) {
      var p = ctx.product;
      var ads = ctx.ads || {};
      ahPosthog('artwork', 'view', {
        event_label: p.title,
        value: p.price,
        handle: p.handle,
        title: p.title,
        artist: p.vendor,
        vendor: p.vendor,
        product_id: p.id,
        product_type: p.type,
        price: p.price,
        collection: p.collection,
        // Set by sections/product-hero.liquid: the Merchant Center item id and the price the
        // visitor is actually shown (presentment currency), for the Google Ads sink.
        item_id: ads.item_id,
        presentment_price: ads.price
      });
    }

    // Collection page → collection_view
    if (ctx.collection && ctx.collection.handle) {
      var c = ctx.collection;
      ahPosthog('collection', 'view', {
        event_label: c.title,
        value: c.count,
        collection: c.handle,
        title: c.title,
        product_count: c.count,
        item_ids: c.item_ids || []
      });
    }
  }

  // ── Delegated interaction events ───────────────────────────────────────────────────────
  function bindDelegated() {
    // Discovery quick-filter chips (snippets/concept-chips-bar.liquid). Link chips navigate to
    // a concept collection; rerank chips re-sort the grid in place. Both are a collection_filter.
    document.addEventListener('click', function (e) {
      var t = e.target;

      var rerank = t.closest && t.closest('[data-rerank-seed]');
      if (rerank) {
        ahPosthog('collection', 'filter', {
          event_label: rerank.getAttribute('data-label') || rerank.getAttribute('data-rerank-seed') || '',
          filter_type: 'rerank',
          filter_seed: rerank.getAttribute('data-rerank-seed') || '',
          collection: collectionHandle()
        });
        return;
      }

      var linkChip = t.closest && t.closest('[data-concept-chips] .chip-link');
      if (linkChip) {
        ahPosthog('collection', 'filter', {
          event_label: (linkChip.textContent || '').trim(),
          filter_type: 'concept',
          target: linkChip.getAttribute('href') || '',
          collection: collectionHandle()
        });
        return;
      }

      // Checkout → checkout_begin. Covers the cart-drawer footer link, the cart page button,
      // and any [data-checkout] trigger. Fire-and-forget just before navigation.
      var checkout = t.closest && t.closest('a[href^="/checkout"], a[href*="/checkout"], [name="checkout"], [data-checkout]');
      if (checkout) {
        ahPosthog('checkout', 'begin', {
          event_label: 'checkout',
          event_category: 'commerce'
        });
      }
    }, true);

    // Search overlay (sections/search-overlay.liquid + assets/search.js).
    // Deliberate submits only — Enter in the field, or tapping a suggested prompt — so we do
    // not fire on every debounced keystroke and never on the crawl-driven server path.
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      var input = e.target;
      if (!input || !input.matches || !input.matches('[data-search-input]')) return;
      var q = (input.value || '').trim();
      if (q.length < 2) return;
      ahPosthog('search', 'submit', { event_label: q, query: q, method: 'input' });
      startAiSearchReplay(); // watch how shoppers use the Picasso AI search
    }, true);

    document.addEventListener('click', function (e) {
      var sug = e.target.closest && e.target.closest('[data-search-suggestion]');
      if (!sug) return;
      var q = (sug.getAttribute('data-search-suggestion') || '').trim();
      if (!q) return;
      ahPosthog('search', 'submit', { event_label: q, query: q, method: 'suggestion' });
      startAiSearchReplay(); // watch how shoppers use the Picasso AI search
    }, true);

    // Primary PDP add-to-cart (native Shopify form, snippets/product-form.liquid → /cart/add).
    // The cart-drawer quick-add path is instrumented directly in assets/cart-drawer.js.
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.matches || !form.matches('form[action^="/cart/add"], form[action="/cart/add"]')) return;
      var ctx = (window.__ahAnalytics && window.__ahAnalytics.product) || {};
      var sel = form.querySelector('[name="id"]');
      ahPosthog('cart', 'add', {
        event_label: ctx.title || '',
        value: ctx.price,
        handle: ctx.handle,
        title: ctx.title,
        artist: ctx.vendor,
        variant_id: sel ? sel.value : undefined,
        price: ctx.price,
        source: 'product_form'
      });
    }, true);
  }

  function collectionHandle() {
    var ctx = window.__ahAnalytics || {};
    if (ctx.collection && ctx.collection.handle) return ctx.collection.handle;
    var el = document.querySelector('[data-concept-chips][data-collection-handle]');
    return el ? el.getAttribute('data-collection-handle') : '';
  }

  // ── Confirmed cart adds → bus-only `cart_lines_added` ─────────────────────────────────
  // Every add path on this theme (v2 PDP product-config.js, cart-drawer quick add,
  // gallery-set-cart.js, space-link.js, legacy app.js via jQuery XHR) ends in a POST to
  // /cart/add(.js). Observing the response — rather than instrumenting each caller — gives one
  // event per successful add, with the lines Shopify really created: presentment-currency
  // prices, SKUs and line properties (_artwork_id, _sku, _set_id). A set is one event with all
  // of its pieces. Not sent to PostHog (cart_add above already covers it there).
  var CART_ADD = /\/cart\/add(\.js)?(\?|$)/;

  function requestQuantities(body) {
    try {
      var data = null;
      if (typeof body === 'string') {
        if (body.charAt(0) === '{') data = JSON.parse(body);
        else data = { quantity: new URLSearchParams(body).get('quantity') };
      } else if (body && typeof body.get === 'function') {
        data = { quantity: body.get('quantity') };
      }
      if (!data) return [];
      if (Array.isArray(data.items)) return data.items.map(function (i) { return Number(i.quantity) || 1; });
      return [Number(data.quantity) || 1];
    } catch (e) { return []; }
  }

  function emitCartAdd(json, body) {
    try {
      var items = json && Array.isArray(json.items) ? json.items : (json && json.variant_id ? [json] : []);
      if (!items.length) return;
      var qtys = requestQuantities(body);
      var lines = items.map(function (it, i) {
        return {
          product_id: it.product_id,
          variant_id: it.variant_id,
          sku: it.sku,
          title: it.product_title || it.title,
          properties: it.properties || {},
          price: (typeof it.final_price === 'number' ? it.final_price : it.price) / 100,
          quantity: qtys[i] || 1
        };
      });
      ahTrack('cart_lines_added', { lines: lines });
    } catch (e) { /* never break the storefront */ }
  }

  function observeCartAdds() {
    if (window.__ahCartAddObserved) return;
    window.__ahCartAddObserved = true;

    if (typeof window.fetch === 'function') {
      var nativeFetch = window.fetch;
      window.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var method = (init && init.method) || (input && input.method) || 'GET';
        var p = nativeFetch.apply(this, arguments);
        if (CART_ADD.test(url) && String(method).toUpperCase() === 'POST') {
          var body = init && init.body;
          p.then(function (res) {
            if (!res || !res.ok) return;
            res.clone().json().then(function (json) { emitCartAdd(json, body); }, function () {});
          }, function () {});
        }
        return p;
      };
    }

    if (window.XMLHttpRequest) {
      var open = XMLHttpRequest.prototype.open;
      var send = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function (method, url) {
        this.__ahCartAdd = CART_ADD.test(String(url)) && String(method).toUpperCase() === 'POST';
        return open.apply(this, arguments);
      };
      XMLHttpRequest.prototype.send = function (body) {
        if (this.__ahCartAdd) {
          var xhr = this;
          xhr.addEventListener('load', function () {
            if (xhr.status < 200 || xhr.status >= 300) return;
            try { emitCartAdd(JSON.parse(xhr.responseText), body); } catch (e) { /* not JSON */ }
          });
        }
        return send.apply(this, arguments);
      };
    }
  }
  observeCartAdds();

  function init() {
    firePageEvents();
    bindDelegated();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
